<div class="space-y-6">
  <!-- ── Yoqilgan valyutalar ─────────────────────────────────────────────── -->
  <div class="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
      {{ 'SETTINGS.CURRENCY.ENABLED_TITLE' | translate }}
    </h3>
    <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
      {{ 'SETTINGS.CURRENCY.ENABLED_HINT' | translate }}
    </p>

    <div class="mt-4 space-y-2">
      @for (c of allCurrencies(); track c.code) {
        <div
          class="flex items-center justify-between gap-3 rounded-lg border p-3"
          [class.border-emerald-300]="isSelected(c.code)"
          [class.dark:border-emerald-800]="isSelected(c.code)"
          [class.border-gray-200]="!isSelected(c.code)"
          [class.dark:border-gray-700]="!isSelected(c.code)"
        >
          <label class="flex items-center gap-3 cursor-pointer flex-1">
            <input
              type="checkbox"
              [checked]="isSelected(c.code)"
              [disabled]="c.code === quote()"
              (change)="toggle(c.code)"
              class="w-4 h-4 accent-emerald-600 disabled:opacity-50"
            />
            <span class="font-medium text-gray-900 dark:text-gray-100">{{ c.code }}</span>
            <span class="text-sm text-gray-500 dark:text-gray-400">{{ c.name }}</span>
            <span class="text-sm text-gray-400">{{ c.symbol }}</span>
          </label>

          <!-- Kotirovka valyutasi: kurslar shunga nisbatan kiritiladi -->
          @if (quote() === c.code) {
            <span
              class="text-xs font-medium rounded-full px-2.5 py-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400"
            >
              {{ 'SETTINGS.CURRENCY.QUOTE_BADGE' | translate }}
            </span>
          } @else if (isSelected(c.code)) {
            <button
              type="button"
              (click)="setQuote(c.code)"
              class="text-xs text-gray-500 hover:text-emerald-700 dark:hover:text-emerald-400 underline"
            >
              {{ 'SETTINGS.CURRENCY.MAKE_QUOTE' | translate }}
            </button>
          }
        </div>
      }
    </div>

    <!--
      Kotirovka valyutasi buxgalteriya "bazaviy valyutasi" EMAS. Hisobotlar har
      valyutada alohida chiqadi; bu faqat kurslarni nimaga nisbatan kiritish masalasi.
    -->
    <p class="text-xs text-gray-500 dark:text-gray-400 mt-3">
      {{ 'SETTINGS.CURRENCY.QUOTE_EXPLAIN' | translate }}
    </p>

    <button
      type="button"
      (click)="saveCurrencies()"
      [disabled]="savingCurrencies()"
      class="mt-4 inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
    >
      <lucide-icon [img]="Check" class="w-4 h-4" />
      {{ 'COMMON.SAVE' | translate }}
    </button>
  </div>

  <!-- ── Kurs ────────────────────────────────────────────────────────────── -->
  <div class="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
      {{ 'SETTINGS.CURRENCY.RATES_TITLE' | translate }}
    </h3>
    <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
      {{ 'SETTINGS.CURRENCY.RATES_HINT' | translate }}
    </p>

    @if (ratableCurrencies().length < 2) {
      <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">
        {{ 'SETTINGS.CURRENCY.NEED_TWO' | translate }}
      </p>
    } @else {
      <!-- Joriy kurslar -->
      @if (rates().length > 0) {
        <div class="mt-4 overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="text-left text-gray-500 dark:text-gray-400">
              <tr>
                <th class="py-2">{{ 'SETTINGS.CURRENCY.PAIR' | translate }}</th>
                <th class="py-2">{{ 'SETTINGS.CURRENCY.UPDATED' | translate }}</th>
                <th class="py-2">{{ 'SETTINGS.CURRENCY.AUTHOR' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (r of rates(); track r.fromCurrency + r.toCurrency) {
                <tr class="border-t border-gray-100 dark:border-gray-800">
                  <!-- Yo'nalish har doim to'liq matn: chalkashsa butun hisob ag'dariladi -->
                  <td class="py-2 font-medium text-gray-900 dark:text-gray-100">
                    1 {{ r.fromCurrency }} = {{ r.rate }} {{ r.toCurrency }}
                  </td>
                  <td class="py-2 text-gray-500 dark:text-gray-400">
                    {{ r.effectiveFrom | date: 'dd.MM.yyyy HH:mm' }}
                    @if (r.isStale) {
                      <span
                        class="ml-2 inline-flex items-center gap-1 text-amber-600 dark:text-amber-500"
                      >
                        <lucide-icon [img]="TriangleAlert" class="w-3.5 h-3.5" />
                        {{ 'SETTINGS.CURRENCY.STALE' | translate }}
                      </span>
                    }
                  </td>
                  <td class="py-2 text-gray-500 dark:text-gray-400">
                    {{ r.createdByName || '—' }}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }

      <!-- Yangi kurs -->
      <div class="mt-5 border-t border-gray-100 dark:border-gray-800 pt-4">
        <div class="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div class="flex flex-col gap-1">
            <label for="rate-from" class="text-xs text-gray-500">{{
              'SETTINGS.CURRENCY.FROM' | translate
            }}</label>
            <select
              id="rate-from"
              [(ngModel)]="rateFrom"
              class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
            >
              @for (c of ratableCurrencies(); track c.code) {
                <option [value]="c.code">{{ c.code }}</option>
              }
            </select>
          </div>

          <div class="flex flex-col gap-1">
            <label for="rate-to" class="text-xs text-gray-500">{{
              'SETTINGS.CURRENCY.TO' | translate
            }}</label>
            <select
              id="rate-to"
              [(ngModel)]="rateTo"
              class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
            >
              @for (c of ratableCurrencies(); track c.code) {
                <option [value]="c.code">{{ c.code }}</option>
              }
            </select>
          </div>

          <div class="flex flex-col gap-1">
            <label for="rate-value" class="text-xs text-gray-500">{{
              'SETTINGS.CURRENCY.RATE' | translate
            }}</label>
            <input
              id="rate-value"
              type="number"
              min="0"
              step="any"
              [(ngModel)]="rateValue"
              placeholder="12650"
              class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
            />
          </div>

          <button
            type="button"
            (click)="saveRate()"
            [disabled]="savingRate()"
            class="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            <lucide-icon [img]="Plus" class="w-4 h-4" />
            {{ 'SETTINGS.CURRENCY.ADD_RATE' | translate }}
          </button>
        </div>

        <!-- Kassir ko'rgan raqam bilan yozilgan raqam bir xil bo'lishi uchun oldindan ko'rsatamiz -->
        @if (ratePreview()) {
          <p class="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ ratePreview() }}
          </p>
        }

        <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {{ 'SETTINGS.CURRENCY.APPEND_ONLY' | translate }}
        </p>
      </div>

      <!-- Tarix -->
      <button
        type="button"
        (click)="toggleHistory()"
        class="mt-4 inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-emerald-700 dark:hover:text-emerald-400"
      >
        <lucide-icon [img]="History" class="w-4 h-4" />
        {{ 'SETTINGS.CURRENCY.HISTORY' | translate }}
      </button>

      @if (historyOpen()) {
        <div class="mt-3 overflow-x-auto">
          <table class="w-full text-sm">
            <tbody>
              @for (r of history(); track $index) {
                <tr class="border-t border-gray-100 dark:border-gray-800">
                  <td class="py-2 text-gray-900 dark:text-gray-100">
                    1 {{ r.fromCurrency }} = {{ r.rate }} {{ r.toCurrency }}
                  </td>
                  <td class="py-2 text-gray-500 dark:text-gray-400">
                    {{ r.effectiveFrom | date: 'dd.MM.yyyy HH:mm' }}
                  </td>
                  <td class="py-2 text-gray-500 dark:text-gray-400">
                    {{ r.createdByName || '—' }}
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td class="py-3 text-gray-500 dark:text-gray-400">
                    {{ 'SETTINGS.CURRENCY.NO_HISTORY' | translate }}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    }
  </div>
</div>
